import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CoreModule, Environment, EnvironmentService } from '@galaxy/core';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { AsyncUiModule, EmptyStateModule } from '@vendasta/uikit';
import { TemplateEditorPageComponent } from './template-editor-page/template-editor-page.component';
import { EmailBuilderModule, EmailPreviewHydrationData } from '@galaxy/email-ui/email-builder';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AccountGroupApiService, ListRequest, PagedResponse } from '@vendasta/account-group';
import { AccountGroup } from '@vendasta/account-group/lib/_internal/objects/api';
import { PreviewBusinessContactSelectorModule } from '../../shared/preview-business-contact-selector/preview-business-contact-selector.module';

@NgModule({
  declarations: [TemplateEditorPageComponent],
  exports: [TemplateEditorPageComponent],
  imports: [
    CommonModule,
    GalaxyPageModule,
    MatProgressSpinnerModule,
    AsyncUiModule,
    TranslateModule,
    EmptyStateModule,
    CoreModule, // Needed to auth HTTP requests
    MatDialogModule,
    MatButtonModule,
    GalaxyEmptyStateModule,
    MatIconModule,
    EmailBuilderModule,
    PreviewBusinessContactSelectorModule,
  ],
  providers: [provideHttpClient(withInterceptorsFromDi())],
})
export class BaseTemplateEditingModule {}

export function buildHydrationData(
  partnerId$: Observable<string>,
  marketId$: Observable<string>,
  environmentService: EnvironmentService,
  accountGroups: AccountGroupApiService,
): Observable<EmailPreviewHydrationData> {
  const accountGroup$ = partnerId$.pipe(
    switchMap((partnerId) =>
      accountGroups
        .list(
          new ListRequest({
            pageSize: 1,
            filters: {
              partnerId,
            },
            projectionFilter: {
              napData: true,
              snapshotReports: true,
              accountGroupExternalIdentifiers: true,
            },
          }),
        )
        .pipe(map((v: PagedResponse) => (v.accountGroups || [])[0])),
    ),
  );

  return combineLatest([partnerId$, marketId$, accountGroup$]).pipe(
    map(([partnerId, marketId, accountGroup]: [string, string, AccountGroup]) => ({
      business: {
        accountGroupId: accountGroup.accountGroupId,
        name: accountGroup.napData?.companyName,
        hasSnapshot: accountGroup.snapshotReports?.snapshots?.length > 0,
        hasSalesperson: !!accountGroup.accountGroupExternalIdentifiers.salesPersonId,
      },
      userID: getExampleUserId(environmentService.getEnvironment()),
      contactID: 'Contact',
      partnerID: partnerId,
      marketID: marketId,
      locale: 'en',
    })),
  );
}

function getExampleUserId(env: Environment): string {
  let userId = 'U-862a9c0e-e46d-42ca-9f1b-21303a52ca9c';
  if (env === Environment.PROD) {
    userId = 'U-4c8b64a1-4120-40b6-82d2-f2e0daa2fcc1';
  }
  return userId;
}
